import { __rest, __spreadArray, __assign } from 'tslib';
import React, { useState, useRef, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { Button } from '../../button/esm';
import { KeyboardFocusable } from '../../keyboard-focusable/esm';
import { ProgressBar } from '../../progress-bar/esm';
import { PaperclipMIcon } from '@alfalab/icons-glyph/PaperclipMIcon';
import { PaperclipSIcon } from '@alfalab/icons-glyph/PaperclipSIcon';
import { pluralize } from '@alfalab/utils';
import { truncateFilename } from './utils/index.js';

var styles = {"component":"attach__component_un3rb","label":"attach__label_un3rb","control":"attach__control_un3rb","xxs":"attach__xxs_un3rb","file":"attach__file_un3rb","noFile":"attach__noFile_un3rb","xs":"attach__xs_un3rb","s":"attach__s_un3rb","m":"attach__m_un3rb","l":"attach__l_un3rb","abbreviation":"attach__abbreviation_un3rb","clear":"attach__clear_un3rb","progressBar":"attach__progressBar_un3rb","disabled":"attach__disabled_un3rb","icon":"attach__icon_un3rb","focused":"attach__focused_un3rb"};
require('./index.css');

var MULTIPLE_TEXTS = ['файл', 'файла', 'файлов'];
var Attach = React.forwardRef(function (_a, ref) {
    var _b;
    var _c = _a.size, size = _c === void 0 ? 's' : _c, accept = _a.accept, _d = _a.buttonContent, buttonContent = _d === void 0 ? 'Выберите файл' : _d, _e = _a.buttonProps, buttonProps = _e === void 0 ? {} : _e, className = _a.className, fileClassName = _a.fileClassName, noFileClassName = _a.noFileClassName, disabled = _a.disabled, dataTestId = _a.dataTestId, id = _a.id, maxFilenameLength = _a.maxFilenameLength, multiple = _a.multiple, _f = _a.noFileText, noFileText = _f === void 0 ? 'Нет файла' : _f, progressBarPercent = _a.progressBarPercent, defaultValue = _a.defaultValue, value = _a.value, onChange = _a.onChange, onClear = _a.onClear, restProps = __rest(_a, ["size", "accept", "buttonContent", "buttonProps", "className", "fileClassName", "noFileClassName", "disabled", "dataTestId", "id", "maxFilenameLength", "multiple", "noFileText", "progressBarPercent", "defaultValue", "value", "onChange", "onClear"]);
    var uncontrolled = value === undefined;
    var _g = useState(defaultValue || []), files = _g[0], setFiles = _g[1];
    var inputRef = useRef(null);
    var labelRef = useRef(null);
    var buttonRef = useRef(null);
    var getDefaultLeftAddon = function () {
        var IconComponent;
        if (['xs', 'xxs'].includes(size)) {
            IconComponent = PaperclipSIcon;
        }
        else {
            IconComponent = PaperclipMIcon;
        }
        return React.createElement(IconComponent, { className: styles.icon });
    };
    var handleInputChange = function (event) {
        var filesArray = event.target.files ? Array.from(event.target.files) : [];
        if (onChange) {
            onChange(event, { files: filesArray });
        }
        if (uncontrolled && event.target.files) {
            setFiles(filesArray);
        }
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    };
    var handleButtonClick = function (event) {
        if (labelRef.current) {
            labelRef.current.click();
        }
        if (buttonRef.current) {
            buttonRef.current.focus();
        }
        if (buttonProps.onClick) {
            buttonProps.onClick(event);
        }
    };
    var handleClearClick = function (ev) {
        if (uncontrolled) {
            setFiles([]);
        }
        if (onClear) {
            onClear(ev);
        }
    };
    var statusTextContent = files.length === 1 ? (truncateFilename(files[0].name, maxFilenameLength)) : (React.createElement("abbr", { className: styles.abbreviation, title: files.map(function (file) { return file.name; }).join() },
        files.length,
        " ",
        pluralize.apply(void 0, __spreadArray([files.length], MULTIPLE_TEXTS, false))));
    useEffect(function () {
        if (!uncontrolled) {
            setFiles(value || []);
        }
    }, [uncontrolled, value]);
    return (React.createElement("div", { className: cn(styles.component, styles[size], (_b = {},
            _b[styles.disabled] = disabled,
            _b), className) },
        React.createElement(Button, __assign({}, buttonProps, { size: size, disabled: disabled, view: (buttonProps === null || buttonProps === void 0 ? void 0 : buttonProps.view) || 'secondary', leftAddons: (buttonProps === null || buttonProps === void 0 ? void 0 : buttonProps.leftAddons) || getDefaultLeftAddon(), onClick: handleButtonClick, ref: buttonRef }),
            React.createElement("span", null, buttonContent)),
        React.createElement("label", { className: styles.label, htmlFor: id, ref: labelRef },
            React.createElement("input", __assign({}, restProps, { className: styles.control, accept: accept, disabled: disabled, id: id, multiple: multiple, tabIndex: -1, type: 'file', onChange: handleInputChange, ref: mergeRefs([ref, inputRef]), "data-test-id": dataTestId }))),
        files && files.length > 0 ? (React.createElement("div", { className: cn(styles.file, fileClassName) },
            React.createElement("span", null, statusTextContent),
            React.createElement(KeyboardFocusable, null, function (targetRef, focused) {
                var _a;
                return (React.createElement("button", { "aria-label": '\u043E\u0447\u0438\u0441\u0442\u0438\u0442\u044C', type: 'button', className: cn(styles.clear, (_a = {},
                        _a[styles.focused] = focused,
                        _a)), onClick: handleClearClick, ref: targetRef }));
            }),
            progressBarPercent && (React.createElement(ProgressBar, { className: styles.progressBar, value: progressBarPercent, view: 'positive' })))) : (React.createElement("div", { className: cn(styles.noFile, noFileClassName) }, noFileText))));
});
/**
 * Для отображения в сторибуке
 */
Attach.defaultProps = {
    size: 's',
    buttonContent: 'Выберите файл',
    noFileText: 'Нет файла',
};

export { Attach };
